import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import PlayerForm, { PlayerFormData } from '@/components/players/PlayerForm';
import ActionButtons from '@/components/ui/ActionButtons';
import Notification from '@/components/ui/Notification';
import { createPlayer, getErrorMessage, getPlayer, updatePlayer } from '@/lib/api';

export function PlayerDetail() {
  const { t } = useTranslation(['players', 'common']);
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const idParam = searchParams.get('id');
  const playerId = idParam ? parseInt(idParam, 10) : null;

  // Determine if in edit mode
  const isEditMode = location.pathname.includes('/edit');

  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(isEditMode);

  // Initial data state
  const [initialFormData, setInitialFormData] = useState<PlayerFormData>({
    playerCode: '',
    apiKey: '',
    apiSecret: '',
    name: '',
  });

  // Notification state
  const [notificationOpen, setNotificationOpen] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState('');
  const [notificationSeverity, setNotificationSeverity] = useState<'success' | 'error'>('success');

  // Fetch player data if in edit mode
  useEffect(() => {
    if (!isEditMode) return;

    const fetchPlayerData = async () => {
      if (!playerId) {
        setNotificationMessage(t('players:messages.noPlayerId'));
        setNotificationSeverity('error');
        setNotificationOpen(true);
        setInitialLoading(false);
        return;
      }

      try {
        const response = await getPlayer(playerId);
        const player = response.data;

        setInitialFormData({
          playerCode: player.playerCode,
          apiKey: player.apiKey,
          apiSecret: player.apiSecret,
          name: player.name || '',
        });
      } catch (err) {
        console.error('Failed to fetch player:', err);
        setNotificationMessage(getErrorMessage(err));
        setNotificationSeverity('error');
        setNotificationOpen(true);
      } finally {
        setInitialLoading(false);
      }
    };

    fetchPlayerData();
  }, [playerId, isEditMode]);

  /**
   * Player creation/update process
   */
  const handleSubmit = async (formData: PlayerFormData) => {
    setLoading(true);

    try {
      if (isEditMode && playerId) {
        // Edit mode: Update player using API
        const result = await updatePlayer(playerId, formData);
        setNotificationMessage(result.meta.message || t('players:messages.updateSuccess'));
      } else {
        // Create mode: Create player using API
        await createPlayer(formData);
        setNotificationMessage(t('players:messages.createSuccess'));
      }

      // Display success message
      setNotificationSeverity('success');
      setNotificationOpen(true);

      // Return to list page after a short delay
      setTimeout(() => {
        navigate('/players');
      }, 1500);
    } catch (err) {
      console.error(`Failed to ${isEditMode ? 'update' : 'create'} player:`, err);
      // Convert error message to user-friendly format
      setNotificationMessage(getErrorMessage(err));
      setNotificationSeverity('error');
      setNotificationOpen(true);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Close notification
   */
  const handleCloseNotification = () => {
    setNotificationOpen(false);
  };

  /**
   * Return to list page
   */
  const handleBack = () => {
    navigate('/players');
  };

  // Set page title and button label
  const pageTitle = isEditMode ? t('players:titles.edit') : t('players:titles.create');
  const submitLabel = isEditMode ? t('common:buttons.update') : t('common:buttons.create');

  if (initialLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={handleBack}
            sx={{ mr: 2 }}
            disabled={loading}
          >
            {t('common:buttons.back')}
          </Button>
          <Typography variant="h4" component="h1">
            {pageTitle}
          </Typography>
        </Box>
      </Box>

      <Paper sx={{ p: 3, mb: 3 }}>
        <PlayerForm
          initialData={initialFormData}
          onSubmit={handleSubmit}
          submitLabel={submitLabel}
          loading={loading}
          showId={isEditMode}
          playerId={playerId || undefined}
        />
        <Box sx={{ mt: 4 }}>
          <ActionButtons
            onCancel={handleBack}
            cancelLabel={t('common:buttons.back')}
            buttonOrder="cancel-delete-submit"
            sx={{ justifyContent: 'flex-start' }}
          />
        </Box>
      </Paper>

      {/* Notification */}
      <Notification
        open={notificationOpen}
        message={notificationMessage}
        severity={notificationSeverity}
        onClose={handleCloseNotification}
      />
    </>
  );
}

export default PlayerForm;
